import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Table, Tag, Button, Modal, Form, Input, Select, Space, DatePicker, message, Card, ColorPicker } from 'antd';
import { collectDirectServerIds } from '../utils/projectDirectServers';
import { EditOutlined, DeleteOutlined, PlusOutlined, ProjectOutlined, EyeOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import {
  useListProjectsQuery, useAddProjectMutation, useEditProjectMutation, useDeleteProjectMutation, useListClientsQuery,
  useListAppsQuery, useAddAppMutation, useEditAppMutation, useDeleteAppMutation,
} from '../store/api';

const { Option } = Select;

const Proyectos = () => {
  const navigate = useNavigate();
  const { data = [], isLoading } = useListProjectsQuery();
  const { data: clients = [] } = useListClientsQuery();
  const { data: apps = [], refetch: refetchApps } = useListAppsQuery();
  const [addProject] = useAddProjectMutation();
  const [editProject] = useEditProjectMutation();
  const [deleteProject] = useDeleteProjectMutation();
  const [addApp] = useAddAppMutation();
  const [editApp] = useEditAppMutation();
  const [deleteApp] = useDeleteAppMutation();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [appsModal, setAppsModal] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [editingApp, setEditingApp] = useState(null);
  const [form] = Form.useForm();
  const [appForm] = Form.useForm();
  const [search, setSearch] = useState('');

  const filtered = data.filter((p) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    const blob = [p.nombre, p.descripcion, p.url_produccion, p.tech_stack, p.estado, p.como_funciona]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();
    return blob.includes(q) || String(p.id) === q;
  });

  const showModal = (record = null) => {
    setEditingRecord(record);
    record ? form.setFieldsValue({ ...record, fecha_inicio: record.fecha_inicio ? dayjs(record.fecha_inicio) : null, color: record.color || '#0168fa' }) : form.resetFields();
    setIsModalVisible(true);
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      const colorVal = typeof values.color === 'string' ? values.color : values.color?.toHexString?.() || '#0168fa';
      const fv = { ...values, color: colorVal, fecha_inicio: values.fecha_inicio?.format('YYYY-MM-DD') || null };
      if (editingRecord?.id) {
        await editProject({ id: editingRecord.id, ...fv }).unwrap();
        message.success('Proyecto actualizado');
      } else {
        await addProject(fv).unwrap();
        message.success('Proyecto creado');
      }
      setIsModalVisible(false);
      form.resetFields();
    } catch {
      message.error('Error al guardar proyecto');
    }
  };

  const handleDelete = (id) => {
    Modal.confirm({
      title: '¿Eliminar proyecto?', content: 'Esta acción no se puede deshacer.',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await deleteProject(id).unwrap();
          message.success('Proyecto eliminado');
        } catch { message.error('Error al eliminar'); }
      },
    });
  };

  const getClientName = (id) => clients.find(c => c.id === id)?.nombre || id;

  const columns = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 60 },
    { title: 'Proyecto', dataIndex: 'nombre', key: 'nombre', render: (t, r) => (
      <span style={{ fontWeight: 600, cursor: 'pointer', color: '#0168fa' }} onClick={() => navigate(`/proyecto/${r.id}`)}>
        <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: r.color || '#0168fa', marginRight: 6 }} />
        {t}
      </span>
    )},
    { title: 'App', dataIndex: 'app_nombre', key: 'app', width: 120, render: (t, r) => t || apps.find(a => a.id === r.app_id)?.nombre || '—' },
    { title: 'Cliente', dataIndex: 'cliente_id', key: 'cliente_id', render: id => getClientName(id) },
    { title: 'Función', dataIndex: 'descripcion', key: 'descripcion', ellipsis: true, width: 200 },
    { title: 'Srv.', key: 'srv_count', width: 56, align: 'center',
      render: (_, r) => collectDirectServerIds(r).length },
    { title: 'URL', dataIndex: 'url_produccion', key: 'url', width: 150, ellipsis: true,
      render: u => u ? <a href={u} target="_blank" rel="noreferrer">{u.replace(/^https?:\/\//, '')}</a> : '-' },
    { title: 'URL administrativa', dataIndex: 'url_admin', key: 'url_admin', width: 160, ellipsis: true,
      render: u => u ? <a href={u} target="_blank" rel="noreferrer">{u.replace(/^https?:\/\//, '')}</a> : '-' },
    { title: 'Credenciales', dataIndex: 'credenciales_acceso', key: 'credenciales', width: 140, ellipsis: true,
      render: t => t ? <span title={t}>{String(t).split('\n')[0]}</span> : '-' },
    { title: 'Tech Stack', dataIndex: 'tech_stack', key: 'tech_stack', render: t => t ? <Tag color="purple">{t}</Tag> : '-' },
    { title: 'Fecha Inicio', dataIndex: 'fecha_inicio', key: 'fecha_inicio' },
    { title: 'Estado', dataIndex: 'estado', key: 'estado', render: s => {
        const color = s === 'Completado' ? 'green' : s === 'Pendiente' ? 'orange' : 'geekblue';
        return <Tag color={color} style={{ borderRadius: 12 }}>{s?.toUpperCase() || 'N/A'}</Tag>;
    }},
    { title: 'Acciones', key: 'acciones', render: (_, record) => (
        <Space size={4}>
          <button className="action-btn" title="Ver Detalle" onClick={() => navigate(`/proyecto/${record.id}`)}><EyeOutlined /></button>
          <button className="action-btn" title="Editar" onClick={() => showModal(record)}><EditOutlined /></button>
          <button className="action-btn" title="Eliminar" onClick={() => handleDelete(record.id)} style={{ color: '#dc3545' }}><DeleteOutlined /></button>
        </Space>
    )},
  ];

  return (
    <div className="page-container fade-in">
      <div className="page-header">
        <div className="page-header-left">
          <h1 className="page-title"><ProjectOutlined style={{ marginRight: 8 }} />Proyectos</h1>
          <div className="page-subtitle">{filtered.length} de {data.length} proyectos</div>
        </div>
        <Space>
          <Input.Search
            allowClear
            placeholder="Buscar por nombre, URL, stack, ID…"
            style={{ width: 280 }}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        <Button onClick={() => { setEditingApp(null); appForm.resetFields(); setAppsModal(true); }}>
          Gestionar apps
        </Button>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => showModal(null)}
          style={{ borderRadius: 8, fontWeight: 600, background: 'linear-gradient(135deg, #0168fa, #042893)', border: 'none' }}>
          Nuevo Proyecto
        </Button>
        </Space>
      </div>

      <Card bordered={false} bodyStyle={{ padding: 0 }}>
        <div className="modern-table">
          <Table columns={columns} dataSource={filtered} rowKey="id" loading={isLoading}
            pagination={{ pageSize: 25, showTotal: t => `${t} proyectos` }} scroll={{ x: true }} size="middle" />
        </div>
      </Card>

      <Modal title={editingRecord ? 'Editar Proyecto' : 'Nuevo Proyecto'} open={isModalVisible}
        onOk={handleSave} onCancel={() => { setIsModalVisible(false); form.resetFields(); }}
        okText="Guardar" cancelText="Cancelar">
        <Form form={form} layout="vertical">
          <Form.Item name="nombre" label="Nombre del Proyecto" rules={[{ required: true, message: 'Ingrese el nombre' }]}><Input /></Form.Item>
          <Form.Item name="descripcion" label="Función del sitio (breve)"><Input.TextArea rows={2} placeholder="Qué hace este proyecto..." /></Form.Item>
          <Form.Item name="como_funciona" label="Cómo funciona (detalle, Markdown)">
            <Input.TextArea rows={5} placeholder="Flujo para el usuario, APIs, BD y servidores..." />
          </Form.Item>
          <Form.Item name="cliente_id" label="Cliente" rules={[{ required: true, message: 'Seleccione un cliente' }]}>
            <Select>{clients.map(c => <Option key={c.id} value={c.id}>{c.nombre}</Option>)}</Select>
          </Form.Item>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
            <Form.Item name="app_id" label="App">
              <Select allowClear placeholder="Sin app">{apps.map(a => <Option key={a.id} value={a.id}>{a.nombre}</Option>)}</Select>
            </Form.Item>
            <Form.Item name="orden_dashboard" label="Orden dashboard" initialValue={0}>
              <Input type="number" />
            </Form.Item>
            <Form.Item name="mostrar_en_dashboard" label="Mostrar en dashboard" initialValue={1}>
              <Select>
                <Option value={1}>Sí</Option>
                <Option value={0}>No</Option>
              </Select>
            </Form.Item>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Form.Item name="fecha_inicio" label="Fecha de Inicio"><DatePicker style={{ width: '100%' }} /></Form.Item>
            <Form.Item name="estado" label="Estado" initialValue="Pendiente">
              <Select>
                <Option value="Pendiente">Pendiente</Option>
                <Option value="En Progreso">En Progreso</Option>
                <Option value="Completado">Completado</Option>
                <Option value="Cancelado">Cancelado</Option>
              </Select>
            </Form.Item>
          </div>
          <Form.Item name="tech_stack" label="Tech Stack"><Input placeholder="React, Node.js, MySQL..." /></Form.Item>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Form.Item name="url_produccion" label="URL (producción)"><Input placeholder="https://..." /></Form.Item>
            <Form.Item name="url_admin" label="URL administrativa"><Input placeholder="https://.../admin" /></Form.Item>
          </div>
          <Form.Item name="url_staging" label="URL Staging"><Input placeholder="https://..." /></Form.Item>
          <Form.Item name="credenciales_acceso" label="Credenciales">
            <Input.TextArea rows={3} placeholder="Usuario, contraseña y notas de acceso" />
          </Form.Item>
          <Form.Item name="manual_agente" label="Manual de agente (ruta o URL)">
            <Input placeholder="G:\...\AGENTS.md o https://..." />
          </Form.Item>
          <Form.Item name="repositorio" label="Repositorio"><Input placeholder="https://github.com/..." /></Form.Item>
          <Form.Item name="color" label="Color" initialValue="#0168fa"><ColorPicker /></Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Gestionar apps"
        open={appsModal}
        onCancel={() => { setAppsModal(false); setEditingApp(null); appForm.resetFields(); }}
        footer={null}
        width={720}
      >
        <Form
          form={appForm}
          layout="inline"
          style={{ marginBottom: 16, gap: 8, flexWrap: 'wrap' }}
          onFinish={async (values) => {
            try {
              if (editingApp?.id) {
                await editApp({ id: editingApp.id, ...values }).unwrap();
                message.success('App actualizada');
              } else {
                await addApp(values).unwrap();
                message.success('App creada');
              }
              setEditingApp(null);
              appForm.resetFields();
              refetchApps();
            } catch {
              message.error('Error al guardar app');
            }
          }}
        >
          <Form.Item name="nombre" rules={[{ required: true }]}><Input placeholder="Nombre" /></Form.Item>
          <Form.Item name="slug"><Input placeholder="slug" /></Form.Item>
          <Form.Item name="color" initialValue="#0168fa"><Input placeholder="#color" style={{ width: 110 }} /></Form.Item>
          <Form.Item name="orden" initialValue={0}><Input type="number" placeholder="Orden" style={{ width: 90 }} /></Form.Item>
          <Button type="primary" htmlType="submit">{editingApp ? 'Actualizar' : 'Crear'}</Button>
          {editingApp ? <Button onClick={() => { setEditingApp(null); appForm.resetFields(); }}>Cancelar</Button> : null}
        </Form>
        <Table
          size="small"
          rowKey="id"
          dataSource={apps}
          pagination={false}
          columns={[
            { title: 'Nombre', dataIndex: 'nombre' },
            { title: 'Slug', dataIndex: 'slug' },
            { title: 'Orden', dataIndex: 'orden', width: 70 },
            { title: 'Proyectos', dataIndex: 'proyectos_count', width: 90 },
            {
              title: '',
              width: 120,
              render: (_, r) => (
                <Space>
                  <Button size="small" onClick={() => { setEditingApp(r); appForm.setFieldsValue(r); }}>Editar</Button>
                  <Button
                    size="small"
                    danger
                    onClick={() => {
                      Modal.confirm({
                        title: `¿Eliminar ${r.nombre}?`,
                        onOk: async () => {
                          try {
                            await deleteApp(r.id).unwrap();
                            message.success('Eliminada');
                            refetchApps();
                          } catch (e) {
                            message.error(e?.data?.error || 'No se pudo eliminar');
                          }
                        },
                      });
                    }}
                  >
                    Borrar
                  </Button>
                </Space>
              ),
            },
          ]}
        />
      </Modal>
    </div>
  );
};

export default Proyectos;
